import React, { createContext, useContext, useCallback, useState, useEffect } from "react";
import { useRandomData } from "./RandomDataContext";
import { useCustomizedData } from "./CustomizedDataContext";
import { useSeedConfigurations } from "../../../Api/hooks";
import { convertEmployeeNamesToIds } from "../../../Utils/employeeUtils";
import { ENVIRONMENTS, SEED_CONFIGURATION_OPTIONS } from "./constants";
import {
	removeIds,
	removeIdsAndConvertDates,
	processGroups,
	processGoals,
	processReviewCycles,
	processEntities,
	createConfigurationData,
} from "./utils";
import {
	createSeededCompany,
	getWorkflowRunStatusById,
	getWorkflowRunLogsById,
	fetchConfigurationById,
	updateExistingConfiguration,
	createNewConfiguration,
	deleteExistingConfiguration,
} from "./api";

const ConfigurationContext = createContext();

/**
 * Custom hook for accessing the configuration context.
 * It provides an interface to the `ConfigurationContext` and ensures that it is
 * used within a `ConfigurationProvider`, throwing an error otherwise.
 *
 * @returns {Object} The value from the configuration context.
 * @throws {Error} If the hook is used outside of a `ConfigurationProvider`.
 */
const useConfiguration = () => {
	const context = useContext(ConfigurationContext);
	if (!context) {
		throw new Error("useConfiguration must be used within a ConfigurationProvider");
	}
	return context;
};

/**
 * Provides the configuration context to its children.
 * This component manages all state related to seed configurations, including loading,
 * creating, updating, and deleting configurations, as well as handling data for
 * random and customized seeding.
 *
 * @param {object} props - The component props.
 * @param {React.ReactNode} props.children - The child components to be wrapped by the provider.
 * @returns {JSX.Element} The provider component.
 */
const ConfigurationProvider = ({ children }) => {
	//==================================================================================================
	// State and Hook Initialization
	//==================================================================================================

	const { randomDataConfig, updateRandomDataConfig, resetRandomDataConfig } = useRandomData();
	const {
		employeeData,
		customFieldData,
		employeeGroupData,
		customReviewFormData,
		reviewTemplateData,
		reviewCycleData,
		goalsData,
		updateEmployeeData,
		updateCustomFieldData,
		updateCustomReviewFormData,
		updateReviewTemplateData,
		updateReviewCycleData,
		updateEmployeeGroupData,
		updateGoalsData,
		resetAllCustomizedData,
	} = useCustomizedData();

	const { data: seedConfigurationsData, isLoading: seedConfigurationsLoading } = useSeedConfigurations();

	const [config, setConfig] = useState({
		seedConfiguration: null,
		seedConfigurationName: "",
		seedCompanyName: "",
		environment: ENVIRONMENTS[0],
		seedConfigurationOptions: [],
	});

	const [error, setError] = useState(null);

	//==================================================================================================
	// Error Handling
	//==================================================================================================

	const showError = (message) => {
		setError(message);
	};

	const hideError = () => {
		setError(null);
	};

	//==================================================================================================
	// Data Transformation
	//==================================================================================================

	// Transform seed configurations for select options
	const seedConfigurations = (seedConfigurationsData?.data?.data ?? []).map((seedConfiguration) => {
		const configName = Object.keys(seedConfiguration.seedConfiguration)[0];

		return {
			value: seedConfiguration._id,
			label: configName,
			seedConfigurationId: seedConfiguration._id,
		};
	});

	// Get currently selected seed configuration for UI
	const selectedSeedConfiguration = config.seedConfiguration?.seedConfigurationId
		? seedConfigurations.find((s) => s.value === config.seedConfiguration.seedConfigurationId)
		: null;

	//==================================================================================================
	// Event Handlers
	//==================================================================================================

	/**
	 * Handles changes to the selected seed configuration.
	 * Updates the component's state with the new configuration's ID when a selection
	 * is made, or clears it if the selection is removed.
	 *
	 * @param {Object|null} value - The selected configuration object, which includes a `value` property, or `null` to clear the selection.
	 */
	const handleConfigurationChange = useCallback((value) => {
		if (value) {
			setConfig((prev) => ({
				...prev,
				seedConfiguration: { seedConfigurationId: value.value },
			}));
		} else {
			setConfig((prev) => ({
				...prev,
				seedConfiguration: null,
			}));
		}
	}, []);

	//==================================================================================================
	// Effects
	//==================================================================================================

	/**
	 * Effect to automatically load or clear configuration data.
	 * This `useEffect` hook triggers whenever the `config.seedConfiguration` dependency changes.
	 * - If a seed configuration is selected, it calls `loadConfiguration` to fetch its data.
	 *   Upon successful loading, it parses the data to populate the UI, including the
	 *   configuration name, company name, environment, and selected options. It also handles
	 *   various data formats for backward compatibility.
	 * - If no configuration is selected (i.e., `config.seedConfiguration` is null),
	 *   it resets all configuration fields and clears the data in both the random and
	 *   customized data contexts.
	 */
	useEffect(() => {
		if (config.seedConfiguration) {
			loadConfiguration(config.seedConfiguration).then((configData) => {
				if (configData) {
					const configName = configData.config_name || "";
					const seedCompanyName = configData.company_name || "";

					const environmentString = configData.environment;
					const environmentObject =
						ENVIRONMENTS.find((env) => env.value === environmentString) || ENVIRONMENTS[0];

					const enabledOptions = [];
					if (configData.company_functions) {
						if (Array.isArray(configData.company_functions)) {
							configData.company_functions.forEach((funcString) => {
								const parts = funcString.split(":");
								if (parts.length === 2) {
									const key = parts[0].trim();
									const value = parts[1].trim();
									if (value === "enabled" || value === "true") {
										// Skip autoflow as it's handled in feature_flags
										if (key !== "autoflow" && key !== "run_etls") {
											const option = SEED_CONFIGURATION_OPTIONS.find((opt) => opt.id === key);
											if (option) {
												enabledOptions.push(option.id);
											}
										}
									}
								}
							});
						} else {
							for (const key of Object.keys(configData.company_functions)) {
								if (
									configData.company_functions[key] === "enabled" ||
									configData.company_functions[key] === true
								) {
									// Skip autoflow as it's handled in feature_flags
									if (key !== "autoflow" && key !== "run_etls") {
										const option = SEED_CONFIGURATION_OPTIONS.find((opt) => opt.id === key);
										if (option) {
											enabledOptions.push(option.id);
										}
									}
								}
							}
						}
					}

					// Handle autoflow from feature_flags
					if (configData.feature_flags?.autoflow) {
						const autoflowOption = SEED_CONFIGURATION_OPTIONS.find((opt) => opt.id === "autoflow");
						if (autoflowOption) {
							enabledOptions.push(autoflowOption.id);
						}
					}

					// Handle run_etls from root config
					if (configData.run_etls) {
						const runETLsOption = SEED_CONFIGURATION_OPTIONS.find((opt) => opt.id === "runETLs");
						if (runETLsOption) {
							enabledOptions.push(runETLsOption.id);
						}
					}

					setConfig((prev) => ({
						...prev,
						seedConfigurationName: configName,
						seedCompanyName: seedCompanyName,
						environment: environmentObject,
						seedConfigurationOptions: enabledOptions,
					}));
				}
			});
		} else {
			setConfig((prev) => ({
				...prev,
				seedConfigurationName: "",
				seedCompanyName: "",
				environment: ENVIRONMENTS[0],
				seedConfigurationOptions: [],
			}));
			resetAllCustomizedData();
			resetRandomDataConfig();
		}
	}, [config.seedConfiguration, loadConfiguration, resetAllCustomizedData, resetRandomDataConfig, setConfig]);

	//==================================================================================================
	// Core Logic
	//==================================================================================================

	/**
	 * Loads the data for a specific seed configuration.
	 * Fetches the configuration by its ID and populates the random and customized
	 * data contexts with its contents. If no configuration is found or an error
	 * occurs, it resets the data contexts.
	 *
	 * @param {Object} seedConfiguration - The seed configuration object to load, containing a `seedConfigurationId`.
	 * @returns {Promise<Object|null>} A promise that resolves with the loaded configuration data, or `null` if not found.
	 */
	const loadConfiguration = useCallback(
		async (seedConfiguration) => {
			if (!seedConfiguration?.seedConfigurationId) {
				resetRandomDataConfig();
				resetAllCustomizedData();
				return null;
			}

			try {
				const configData = await fetchConfigurationById(seedConfiguration.seedConfigurationId);

				if (!configData) {
					console.warn("No configuration data found for ID:", seedConfiguration.seedConfigurationId);
					return null;
				}

				resetAllCustomizedData();
				resetRandomDataConfig();

				if (configData.random_seeds) {
					const randomConfigUpdate = {
						review_cycle: parseInt(configData.random_seeds.review_cycle) || 0,
						billing_admin: parseInt(configData.random_seeds.billing_admin) || 0,
						admin: parseInt(configData.random_seeds.admin) || 0,
						basic_admin: parseInt(configData.random_seeds.basic_admin) || 0,
						employee_tree_count: parseInt(configData.random_seeds.employee_tree_count) || 0,
						employee_branch_per_tree_count:
							parseInt(configData.random_seeds.employee_branch_per_tree_count) || 0,
						goals: parseInt(configData.random_seeds.goals) || 0,
						review_forms: parseInt(configData.random_seeds.review_forms) || 0,
						percentage_reviews_with_answers:
							parseInt(configData.random_seeds.percentage_reviews_with_answers) || 0,
						percentage_fully_answered_reviews:
							parseInt(configData.random_seeds.percentage_fully_answered_reviews) || 0,
					};
					updateRandomDataConfig(randomConfigUpdate);
				}

				// Handle customized data - always load if present
				if (configData.employees) {
					const employeesWithIds = configData.employees.map((employee, index) => ({
						...employee,
						id: index + 1,
					}));

					updateEmployeeData({
						employees: employeesWithIds || [],
						employeeName: "",
						employeeManager: "",
						employeeRole: "",
					});
				}

				// Update custom fields if present
				if (configData.customFields || configData.fields) {
					const fieldsData = configData.customFields || configData.fields;
					const customFieldsWithIds = fieldsData.map((field, index) => ({
						...field,
						id: index + 1,
					}));

					updateCustomFieldData({
						customFields: customFieldsWithIds || [],
						customFieldName: "",
						customFieldType: "",
					});
				}

				// Update employee groups if present
				if (configData.groups) {
					const employeesWithIds =
						configData.employees?.map((employee, index) => ({
							...employee,
							id: index + 1,
						})) || [];

					const mappedGroups = configData.groups.map((group, index) => ({
						id: index + 1,
						name: group.name,
						employees: convertEmployeeNamesToIds(group.employees || group.employees, employeesWithIds),
						admins: convertEmployeeNamesToIds(group.admins, employeesWithIds),
						adminPermissions: group.adminPermissions || null,
					}));

					updateEmployeeGroupData({
						employeeGroups: mappedGroups || [],
						employeeGroupName: "",
						employeeGroupEmployees: [],
						employeeGroupAdmins: [],
						employeeGroupAdminPermissions: null,
					});
				}

				// Update custom review forms if present
				if (configData.review_forms) {
					const mappedReviewForms = configData.review_forms.map((form, index) => ({
						id: index + 1,
						name: form.name,
						questions: form.questions.map((question, qIndex) => ({
							id: qIndex + 1,
							type: question.type,
							question: question.question,
							choices: question.choices || [],
						})),
					}));

					updateCustomReviewFormData({
						customReviewForms: mappedReviewForms,
						customReviewFormName: "",
						customReviewQuestions: [{ id: Date.now(), type: "", question: "", choices: [] }],
					});
				}

				// Update review templates if present
				if (configData.review_templates) {
					const mappedReviewTemplates = configData.review_templates.map((template, index) => ({
						id: index + 1,
						name: template.name,
						forms: (template.forms || []).map((form, formIndex) => ({
							id: formIndex + 1,
							...form,
							// Ensure boolean values
							visibility: !!form.visibility,
							redact: !!form.redact,
							// Ensure number values
							authors_days_to_complete: parseInt(form.authors_days_to_complete || "0", 10),
							signers_days_to_complete: parseInt(form.signers_days_to_complete || "0", 10),
							// Ensure arrays
							authors: Array.isArray(form.authors) ? form.authors : [form.authors].filter(Boolean),
							signers: Array.isArray(form.signers) ? form.signers : [form.signers].filter(Boolean),
						})),
						forms_360: (template.forms_360 || []).map((form, formIndex) => ({
							id: formIndex + 1,
							...form,
							visibility: !!form.visibility,
							redact: !!form.redact,
							allow_request_message: !!form.allow_request_message,
							authors_days_to_complete: parseInt(form.authors_days_to_complete || "0", 10),
							signers_days_to_complete: parseInt(form.signers_days_to_complete || "0", 10),
							signers: Array.isArray(form.signers) ? form.signers : [form.signers].filter(Boolean),
						})),
						external_forms: (template.external_forms || []).map((form, formIndex) => ({
							id: formIndex + 1,
							...form,
							visibility: !!form.visibility,
							redact: !!form.redact,
						})),
					}));

					updateReviewTemplateData({
						reviewTemplates: mappedReviewTemplates,
						reviewTemplateName: "",
						selectedFormType: null,
						selectedForm: null,
						tempFormData: {},
						forms: [],
						forms_360: [],
						external_forms: [],
					});
				}

				// Update review cycles if present
				if (configData.review_cycles) {
					const employeesWithIds =
						configData.employees?.map((employee, index) => ({
							...employee,
							id: index + 1,
						})) || [];

					const mappedReviewCycles = configData.review_cycles.map((cycle, index) => {
						const mappedSubjects = (cycle.subjects || []).map((subjectName) => {
							const employee = employeesWithIds.find((emp) => emp.name === subjectName);
							return employee ? employee.name : subjectName;
						});

						return {
							id: index + 1,
							name: cycle.name,
							template: cycle.template,
							subjects: mappedSubjects,
							start_date: cycle.start_date || new Date().toISOString(),
							admins_only: !!cycle.admins_only,
							reviews: cycle.reviews || [],
							easy_add_configs: cycle.easy_add_configs || [],
							department: cycle.department || null,
						};
					});

					updateReviewCycleData({
						reviewCycles: mappedReviewCycles || [],
						reviewCycleName: "",
						reviewTemplate: "",
						subjects: [],
						startDate: new Date(),
						admin_only: false,
						reviews: [],
						easy_add_configs: [],
						department: null,
					});
				}

				// Update goals if present
				if (configData.goals) {
					const employeesWithIds =
						configData.employees?.map((employee, index) => ({
							...employee,
							id: index + 1,
						})) || [];

					const goalsWithIds = configData.goals.map((goal, index) => {
						// Handle both single assignee and multiple assignees
						const assigneesList = goal.assignees || (goal.assignee ? [goal.assignee] : []);
						const mappedAssignees = assigneesList.map((assigneeName) => {
							const employee = employeesWithIds.find((emp) => emp.name === assigneeName);
							return employee ? employee.id : assigneeName;
						});

						let mappedOwner = goal.owner;
						if (goal.owner) {
							const ownerEmployee = employeesWithIds.find((emp) => emp.name === goal.owner);
							if (ownerEmployee) {
								mappedOwner = ownerEmployee.id;
							}
						}

						return {
							...goal,
							id: index + 1,
							assignee: mappedAssignees[0] || null, // Set single assignee for backward compatibility
							assignees: mappedAssignees, // Keep full array for multiple assignees
							owner: mappedOwner,
							startDate: goal.startDate ? new Date(goal.startDate) : new Date(),
						};
					});

					updateGoalsData({
						goals: goalsWithIds || [],
						goalId: "",
						goalName: "",
						goalDescription: "",
						goalAssignee: [],
						goalOwner: "",
						goalTarget: "",
						goalUnits: "",
						goalValue: "",
						startDate: new Date(),
					});
				}

				return configData;
			} catch (error) {
				console.error("Error loading configuration:", error);
				return null;
			}
		},
		[
			updateRandomDataConfig,
			resetRandomDataConfig,
			updateEmployeeData,
			updateCustomFieldData,
			updateEmployeeGroupData,
			updateCustomReviewFormData,
			updateReviewTemplateData,
			updateReviewCycleData,
			updateGoalsData,
			resetAllCustomizedData,
		]
	);

	/**
	 * Creates a new seed configuration.
	 * Combines the provided configuration data with random and customized data,
	 * then sends it to the server to be saved as a new configuration.
	 *
	 * @param {Object} configurationData - The configuration data from the UI.
	 * @returns {Promise<Object>} A promise that resolves with the newly created configuration data.
	 * @throws {Error} If the creation process fails.
	 */
	const createConfiguration = async (configurationData) => {
		const completedConfiguration = combineData(configurationData);

		try {
			await createNewConfiguration(completedConfiguration);
			return completedConfiguration;
		} catch (error) {
			console.error("Error saving configuration:", error?.response?.data?.error?.message || error.message);
			throw error;
		}
	};

	/**
	 * Updates an existing seed configuration.
	 * Gathers all relevant data, combines it into a single configuration object,
	 * and sends it to the server to update an existing configuration by its ID.
	 *
	 * @param {Object} configurationData - The configuration data, including the ID of the configuration to update.
	 * @returns {Promise<Object>} A promise that resolves with the updated seed configuration data.
	 * @throws {Error} If the update process fails.
	 */
	const updateConfiguration = async (configurationData) => {
		const completedConfiguration = combineData(configurationData);

		try {
			const response = await updateExistingConfiguration(
				configurationData.seedConfigurationId,
				completedConfiguration
			);

			// Extract seedConfiguration from the nested data structure
			const seedConfiguration = response.data?.data?.seedConfiguration;

			// Update local state with new configuration ID if available
			if (response.data?.data?._id) {
				setConfig((prev) => ({
					...prev,
					seedConfiguration: {
						...prev.seedConfiguration,
						seedConfigurationId: response.data.data._id,
					},
				}));
			}

			return seedConfiguration;
		} catch (error) {
			console.error("Error updating configuration:", error?.response?.data?.error?.message || error.message);
			throw error;
		}
	};

	/**
	 * Deletes a seed configuration by its ID.
	 * Sends a request to the server to remove the specified configuration and then
	 * resets the local data contexts.
	 *
	 * @param {string} configurationId - The unique identifier of the configuration to be deleted.
	 * @throws {Error} If the deletion process fails.
	 */
	const deleteConfiguration = async (configurationId) => {
		try {
			await deleteExistingConfiguration(configurationId);
			resetAllCustomizedData();
			resetRandomDataConfig();
		} catch (error) {
			console.error("Error deleting configuration:", error);
			throw error;
		}
	};

	/**
	 * Creates a copy of an existing seed configuration.
	 * Fetches an existing configuration by its ID, duplicates its data, appends
	 * "(copy)" to its name, and saves it as a new configuration.
	 *
	 * @param {string} configurationId - The ID of the configuration to be copied.
	 * @returns {Promise<Object>} A promise that resolves with the newly created (copied) configuration data.
	 * @throws {Error} If the copy process fails.
	 */
	const copyConfiguration = async (configurationId) => {
		try {
			const configToCopy = await fetchConfigurationById(configurationId);

			if (!configToCopy) {
				console.error("Failed to load configuration to copy");
				return;
			}

			// Process data using shared functions
			const processedGroups = processGroups(configToCopy.groups, configToCopy.employees);
			const processedGoals = processGoals(configToCopy.goals, configToCopy.employees);
			const processedReviewCycles = processReviewCycles(configToCopy.review_cycles, configToCopy.employees);

			// Always include all data fields from the original configuration
			const allDataFields = {
				employees: removeIds(configToCopy.employees) || [],
				fields: removeIds(configToCopy.fields || configToCopy.customFields) || [],
				groups: removeIds(processedGroups) || [],
				review_forms: removeIds(configToCopy.review_forms) || [],
				review_templates: removeIds(configToCopy.review_templates) || [],
				review_cycles: removeIds(processedReviewCycles) || [],
				goals: removeIds(processedGoals) || [],
			};

			const configMetadata = {
				name: `${configToCopy.config_name} (copy)`,
				companyName: configToCopy.company_name,
				companyFunctions: configToCopy.company_functions || [],
				environment: configToCopy.environment || "",
			};

			const copiedConfiguration = createConfigurationData(
				configMetadata,
				allDataFields,
				configToCopy.random_seeds || {}
			);

			await createNewConfiguration(copiedConfiguration);
			return copiedConfiguration;
		} catch (error) {
			console.error("Failed to copy configuration:", error);
			throw error;
		}
	};

	/**
	 * Combines all seeder-related data into a single object.
	 * This function gathers data from the configuration UI, random data settings,
	 * and customized data tables, then processes and structures it into a unified
	 * object ready for API submission.
	 *
	 * @param {Object} configData - The configuration data collected from the UI forms.
	 * @returns {Object} The fully combined and processed data object.
	 */
	const combineData = (configData) => {
		const randomSeedsData = { ...randomDataConfig };

		// Process all entities at once using the new utility
		const processedData = processEntities(
			{
				groups: employeeGroupData.employeeGroups,
				goals: goalsData.goals,
				reviewCycles: reviewCycleData.reviewCycles,
			},
			employeeData.employees
		);

		const allDataFields = {
			employees: removeIdsAndConvertDates(employeeData.employees) || [],
			fields: removeIdsAndConvertDates(customFieldData.customFields) || [],
			groups: removeIdsAndConvertDates(processedData.groups) || [],
			review_forms: removeIdsAndConvertDates(customReviewFormData.customReviewForms) || [],
			review_templates: removeIdsAndConvertDates(reviewTemplateData.reviewTemplates) || [],
			review_cycles: removeIdsAndConvertDates(processedData.reviewCycles) || [],
			goals: removeIdsAndConvertDates(processedData.goals) || [],
		};

		const configMetadata = {
			name: configData.seedConfigurationName,
			companyName: configData.seedCompanyName,
			companyFunctions: (configData.seedConfigurationOptions || []).map((option) => option.value),
			feature_flags: {
				autoflow: configData.seedConfigurationOptions.includes("autoflow"),
			},
			environment: configData.environment?.value || "",
		};

		const result = createConfigurationData(configMetadata, allDataFields, randomSeedsData);
		return result;
	};

	//==================================================================================================
	// Context Provider Value
	//==================================================================================================

	const value = {
		loadConfiguration,
		resetAllCustomizedData,
		resetRandomDataConfig,
		createConfiguration,
		updateConfiguration,
		deleteConfiguration,
		getConfigurationById: fetchConfigurationById,
		config,
		setConfig,
		// Seed configurations list and loading state
		seedConfigurations,
		seedConfigurationsLoading,
		selectedSeedConfiguration,
		handleConfigurationChange,
		copyConfiguration,
		combineData,
		createSeededCompany,
		getWorkflowRunStatusById,
		getWorkflowRunLogsById,
		error,
		showError,
		hideError,
	};

	return <ConfigurationContext.Provider value={value}>{children}</ConfigurationContext.Provider>;
};

export { useConfiguration, ConfigurationProvider };
